import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  Switch,
  Space,
  Toast,
  Tag,
  Popconfirm,
} from "@douyinfe/semi-ui";
import { ApiClient } from "../../api/ApiClient";

const apiClient = ApiClient.getDefault();

interface ExactScoringConfig {
  id?: number;
  fieldCode: string;
  fieldName: string;
  weight: number;
  baseScore: number;
  itemScores: string;
  enabled: boolean;
  sortOrder: number;
}

const FIELD_DICT_MAP: Record<string, string> = {
  house: "houseV2",
  vehicle: "vehicleV2",
  providentFund: "providentFundV2",
  socialSecurity: "socialSecurityV2",
  company: "companyV2",
  credit: "creditV2",
  income: "incomeV2",
  workYears: "workYearsV2",
  loanType: "loanTypeV2",
};

export default function LeadExactScoringConfigPage() {
  const [data, setData] = useState<ExactScoringConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ExactScoringConfig | null>(null);
  const [formApi, setFormApi] = useState<any>();
  const [saving, setSaving] = useState(false);
  const [dictItems, setDictItems] = useState<string[]>([]);
  const [itemScoreMap, setItemScoreMap] = useState<Record<string, number>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.request<ExactScoringConfig[]>(
        "/leadExactScoring/config",
        "GET"
      );
      if (resp.data) setData(resp.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadDictItems = async (fieldCode: string) => {
    const dictCode = FIELD_DICT_MAP[fieldCode];
    if (!dictCode) return;
    try {
      const resp = await apiClient.request<any>("/dicts", "GET");
      if (resp.data) {
        const dict = resp.data.find((d: any) => d.code === dictCode);
        if (dict?.items) setDictItems(dict.items);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleEdit = (record: ExactScoringConfig) => {
    setEditingConfig(record);
    // 解析 itemScores JSON
    let parsed: Record<string, number> = {};
    try {
      parsed = record.itemScores ? JSON.parse(record.itemScores) : {};
    } catch {}
    setItemScoreMap(parsed);
    loadDictItems(record.fieldCode);
    setShowModal(true);
  };

  useEffect(() => {
    if (showModal && formApi && editingConfig) {
      formApi.setValues({
        weight: editingConfig.weight,
        baseScore: editingConfig.baseScore,
        enabled: editingConfig.enabled,
      });
    }
  }, [showModal, formApi]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await formApi!.validate();
      const values = formApi!.getValues();
      const req = {
        id: editingConfig?.id,
        fieldCode: editingConfig!.fieldCode,
        fieldName: editingConfig!.fieldName,
        weight: values.weight,
        baseScore: values.baseScore,
        itemScores: JSON.stringify(itemScoreMap),
        enabled: values.enabled,
        sortOrder: editingConfig!.sortOrder,
      };
      await apiClient.request(
        "/leadExactScoring/config/save",
        "POST",
        undefined,
        req
      );
      Toast.success("保存成功");
      setShowModal(false);
      loadData();
    } catch (e: any) {
      Toast.error(e?.message || "保存失败");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await apiClient.request("/leadExactScoring/config/resetDefaults", "POST");
      Toast.success("已恢复默认配置");
      loadData();
    } catch (e: any) {
      Toast.error(e?.message || "操作失败");
    }
  };

  const handleItemScoreChange = (item: string, score: number) => {
    setItemScoreMap((prev) => ({ ...prev, [item]: score }));
  };

  // 合并字典选项和已有评分项
  const getAllItems = (): string[] => {
    const dictSet = new Set(dictItems);
    Object.keys(itemScoreMap).forEach((k) => dictSet.add(k));
    return Array.from(dictSet);
  };

  const columns = [
    {
      title: "字段",
      dataIndex: "fieldName",
      width: 100,
      render: (t: string, r: ExactScoringConfig) => (
        <Tag color="blue">{t || r.fieldCode}</Tag>
      ),
    },
    {
      title: "权重(满分)",
      dataIndex: "weight",
      width: 90,
      render: (v: number) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title: "基准分",
      dataIndex: "baseScore",
      width: 70,
    },
    {
      title: "评分项数",
      width: 80,
      render: (_: any, r: ExactScoringConfig) => {
        try {
          const map = r.itemScores ? JSON.parse(r.itemScores) : {};
          return Object.keys(map).length;
        } catch {
          return 0;
        }
      },
    },
    {
      title: "启用",
      dataIndex: "enabled",
      width: 60,
      render: (v: boolean) => (
        <Tag color={v ? "green" : "red"}>{v ? "是" : "否"}</Tag>
      ),
    },
    {
      title: "操作",
      width: 80,
      render: (_: any, record: ExactScoringConfig) => (
        <Button size="small" theme="borderless" onClick={() => handleEdit(record)}>
          编辑
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        title="精确评分配置"
        headerExtraContent={
          <Space>
            <Popconfirm
              title="确定恢复默认配置？将清除所有自定义修改"
              onConfirm={handleReset}
            >
              <Button size="small">恢复默认</Button>
            </Popconfirm>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="id"
          size="small"
          bordered
          pagination={false}
        />
      </Card>

      <Modal
        title={`编辑 - ${editingConfig?.fieldName || ""}`}
        visible={showModal}
        onOk={handleSave}
        onCancel={() => setShowModal(false)}
        confirmLoading={saving}
        width={700}
      >
        <Form getFormApi={(api: any) => setFormApi(api)} labelPosition="left" labelWidth={80}>
          <Form.InputNumber
            field="weight"
            label="权重(满分)"
            min={0}
            max={100}
            style={{ width: "100%" }}
            rules={[{ required: true, message: "请填写" }]}
          />
          <Form.InputNumber
            field="baseScore"
            label="基准分"
            style={{ width: "100%" }}
            rules={[{ required: true, message: "请填写" }]}
          />
          <Form.Switch field="enabled" label="启用" />
        </Form>

        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>子项评分</div>
          <div style={{ maxHeight: 400, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#fafafa" }}>
                  <th style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid #eee" }}>选项</th>
                  <th style={{ padding: "6px 8px", textAlign: "center", borderBottom: "1px solid #eee", width: 100 }}>分值</th>
                </tr>
              </thead>
              <tbody>
                {getAllItems().map((item) => (
                  <tr key={item}>
                    <td style={{ padding: "4px 8px", borderBottom: "1px solid #f5f5f5" }}>{item}</td>
                    <td style={{ padding: "4px 8px", borderBottom: "1px solid #f5f5f5", textAlign: "center" }}>
                      <InputNumber
                        value={itemScoreMap[item] ?? 0}
                        onChange={(v: number) => handleItemScoreChange(item, v ?? 0)}
                        min={-30}
                        max={100}
                        size="small"
                        style={{ width: 80 }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </div>
  );
}
